import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { getManagedTeamIds } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

export function useDashboardRoles() {
  const auth = useAuth();
  const user = auth.user;
  const online = useOnlineStatus();
  // Do not trust role/is_manager from offline cached user for privileged UI.
  // Only enable when currently online (fresh /me response) or after refetch on reconnect.
  // role is authoritative: admin = full access; manager = team edit (+ dual readonly group = account read);
  // readonly_admin = view/export only.
  const isAdmin = online && user?.role === "admin";
  const isManagerOnly = online && user?.role === "manager";
  const isReadonlyAdmin = online && user?.role === "readonly_admin";
  /** Dual Entra: manager + readonly groups → account-wide read, team-only edit. */
  const isDualManagerReadonly = isManagerOnly && !!user?.is_readonly_admin_group;
  const canViewReports = isAdmin || isManagerOnly || isReadonlyAdmin;
  // Acknowledge/process field notes: admin + manager only (not readonly).
  const canReviewFieldNotes = isAdmin || isManagerOnly;
  // View field notes section on Admin tab (including readonly viewers).
  const canViewFieldNotesAdmin = isAdmin || isManagerOnly || isReadonlyAdmin;
  const canEditPunches = isAdmin || isManagerOnly;

  const managedTeamQ = useQuery({
    queryKey: ["managed-team-ids", user?.id],
    queryFn: getManagedTeamIds,
    enabled: online && isManagerOnly,
    staleTime: 5 * 60 * 1000,
  });
  const managedEditUserIds = React.useMemo(() => {
    if (isAdmin) return null; // unrestricted
    if (!isManagerOnly) return null;
    // Dual managers: restrict edit UI to team. Manager-only: all visible rows are already team.
    if (!isDualManagerReadonly) return null;
    const ids = managedTeamQ.data?.user_ids ?? [];
    return new Set<number>(ids);
  }, [isAdmin, isManagerOnly, isDualManagerReadonly, managedTeamQ.data?.user_ids]);
  // Portal create-on-behalf (ENABLE_MANAGER_PUNCH_CREATION + admin/manager).
  const canCreateOnBehalf =
    online &&
    (user?.role === "admin" || user?.role === "manager") &&
    user?.enable_manager_punch_creation === true;

  return {
    isAdmin,
    isManagerOnly,
    isReadonlyAdmin,
    isDualManagerReadonly,
    canViewReports,
    canReviewFieldNotes,
    canViewFieldNotesAdmin,
    canEditPunches,
    canCreateOnBehalf,
    managedEditUserIds,
  };
}
